import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarClock, Download, Film, Loader2, Play } from "lucide-react";
import { api } from "@/lib/api";

const LogoBars = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <rect x="2" y="14" width="5" height="12" rx="2.5" fill="#2BA8FF" />
    <rect x="10" y="8" width="5" height="24" rx="2.5" fill="#5AA9FF" />
    <rect x="18" y="4" width="5" height="32" rx="2.5" fill="#8CCBFF" />
    <rect x="26" y="10" width="5" height="20" rx="2.5" fill="#5AA9FF" />
    <rect x="34" y="16" width="5" height="8" rx="2.5" fill="#2BA8FF" />
  </svg>
);

export default function VideoPage() {
  const { vid } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const videoRef = useRef(null);

  const openFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    try { el.play().catch(() => {}); } catch { /* noop */ }
    try {
      if (typeof el.webkitEnterFullscreen === "function") el.webkitEnterFullscreen();
      else if (typeof el.requestFullscreen === "function") el.requestFullscreen().catch(() => {});
      else el.setAttribute("controls", "controls");
    } catch { el.setAttribute("controls", "controls"); }
  };

  useEffect(() => {
    api.get(`/public/video/${vid}`)
      .then((r) => setV(r.data || null))
      .catch(() => setV(null))
      .finally(() => setLoading(false));
  }, [vid]);

  const expired = v && (v.status === "expired" || !v.url);

  return (
    <div className="min-h-screen bg-[#030B18] text-[#F7F9FF] px-4 pb-10"
      style={{ paddingTop: "max(14px, env(safe-area-inset-top))" }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 py-4">
          <LogoBars size={28} />
          <span className="font-extrabold tracking-tight">E-PISNYA</span>
          <span className="text-[#64748B] text-sm">· Відео</span>
        </div>

        {loading && (
          <div className="py-24 grid place-items-center text-[#9FB0C7]">
            <Loader2 size={26} className="animate-spin" />
          </div>
        )}

        {!loading && !v && (
          <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
            <div className="text-4xl mb-3">🎬</div>
            <p className="text-[#9FB0C7] text-sm">Відео не знайдено або посилання невірне.</p>
          </div>
        )}

        {!loading && v && (
          <>
            <div className="mb-4 flex items-start gap-2.5 rounded-2xl border border-[#FFD83D]/25 bg-[#FFD83D]/[0.07] px-4 py-3">
              <CalendarClock size={18} className="text-[#FFD83D] mt-0.5 flex-none" />
              <p className="text-[13px] leading-snug text-[#FFE9A3]">
                Відео зберігається <b>7 днів</b> — радимо завантажити його
                на ваш пристрій.
              </p>
            </div>

            {expired ? (
              <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
                <div className="text-4xl mb-3">🗓</div>
                <p className="text-[#9FB0C7] text-sm">
                  Термін зберігання відео минув — файл видалено.
                </p>
              </div>
            ) : (
              <>
                <div className="relative grid aspect-video place-items-center overflow-hidden rounded-3xl border border-white/10 bg-black"
                  data-testid="video-frame">
                  {v.thumb && (
                    <img src={v.thumb} alt="" className="max-h-full max-w-full object-contain" />
                  )}
                  <button type="button" onClick={openFullscreen} aria-label="Дивитись на весь екран"
                    data-testid="video-play"
                    className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full text-white active:scale-95"
                    style={{ background: "linear-gradient(160deg,#5AA9FF 0%,#3C8BFF 45%,#2F72F5 100%)",
                             boxShadow: "inset 0 1px 0 rgba(255,255,255,.28), 0 10px 28px rgba(47,128,255,.4)" }}>
                    <Play size={26} fill="#fff" color="#fff" style={{ marginLeft: 3 }} />
                  </button>
                  <video ref={videoRef} className="hidden" playsInline preload="metadata"
                    src={v.url} poster={v.thumb || undefined} />
                </div>

                <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-[#5AA9FF]/25 bg-[#1E5AA0]/40">
                    <Film size={20} className="text-[#BFE0FF]" />
                  </span>
                  <span className="min-w-0">
                    <b className="block truncate text-[15px]">{v.song_title || "Відео"}</b>
                    <i className="text-xs not-italic text-[#7284A6]">
                      {v.photos} фото · {v.format}
                    </i>
                  </span>
                </div>

                <a href={v.url} download
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-4 text-[15px] font-extrabold text-white active:scale-[0.99]"
                  style={{ background: "linear-gradient(160deg,#5AA9FF 0%,#3C8BFF 45%,#2F72F5 100%)",
                           boxShadow: "0 10px 26px rgba(47,128,255,.3)" }}
                  data-testid="video-download">
                  <Download size={18} /> Завантажити на пристрій
                </a>

              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
